import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { X, Boxes, Layers } from "lucide-react";
import ApiCollections from "../../configs/services/apiCollections";
import { toastify } from "../../configs/utils";
import { POLICIES } from "../../routes/routes";
import { signOutRequest } from "../../redux/auth/auth.actions";

// Styled Components
const DashboardContainer = styled.div`
  padding: 2rem;
  width: 100%;
  background: #ededed;
  min-height: 100%;
  border-bottom-left-radius: 12px;
  border-bottom-right-radius: 12px;
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const StatCard = styled.div`
  background: transparent;
  border-radius: 12px;
  padding: 1.75rem;
  border: 1px solid rgba(17, 24, 39, 0.08);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04), 0 6px 16px rgba(0, 0, 0, 0.07),
    inset 0 1px 0 rgba(255, 255, 255, 0.6);
  cursor: pointer;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
  display: flex;
  align-items: center;
  gap: 1.25rem;

  &:hover {
    border-color: rgba(17, 24, 39, 0.16);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05), 0 10px 24px rgba(0, 0, 0, 0.1),
      inset 0 1px 0 rgba(255, 255, 255, 0.6);
    transform: translateY(-2px);
  }
`;

const StatIcon = styled.div`
  width: 48px;
  height: 48px;
  flex-shrink: 0;
  border-radius: 10px;
  background: ${(props) => props.$bg || "#eff6ff"};
  color: ${(props) => props.$color || "#3b82f6"};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StatCardBody = styled.div`
  min-width: 0;
`;

const StatCardTitle = styled.h3`
  font-size: 0.8125rem;
  font-weight: 600;
  margin: 0 0 0.25rem 0;
  color: #6b7280;
`;

const StatCardValue = styled.div`
  font-size: 1.75rem;
  font-weight: 700;
  color: #111827;
  line-height: 1.2;
`;

const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4rem;
  color: #6b7280;
  font-size: 1.1rem;
`;

const ErrorState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4rem;
  color: #ef4444;
  font-size: 1.1rem;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
`;

const PolicyAlertContainer = styled.div`
  width: 100%;
  background: #fef3c7;
  border: 1px solid #fbbf24;
  border-radius: 8px;
  padding: 1rem 1.5rem;
  margin-bottom: 2rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
`;

const PolicyAlertMessage = styled.p`
  margin: 0;
  color: #92400e;
  font-size: 0.9rem;
  font-weight: 500;
`;

const PolicyModalButton = styled.button`
  background: #f59e0b;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #d97706;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  &:active {
    transform: translateY(0);
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${props => props.$open ? "flex" : "none"};
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: 12px;
  padding: 0;
  max-width: 800px;
  width: 90%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  position: relative;
`;

const ModalHeader = styled.div`
  padding: 1.5rem 2rem;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
`;

const ModalTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  color: #111827;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: all 0.2s ease;

  &:hover {
    background: #f3f4f6;
    color: #111827;
  }
`;

const ModalContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f9fafb;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 3px;

    &:hover {
      background: #9ca3af;
    }
  }
`;

const PolicySection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const PolicySectionTitle = styled.h4`
  font-size: 1.125rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
  padding-bottom: 0.75rem;
  border-bottom: 2px solid #e5e7eb;
`;

const PolicyList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const PolicyItem = styled.div`
  padding: 1rem;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  display: flex;
  gap: 1rem;
  align-items: flex-start;
`;

const RadioButton = styled.input`
  margin-top: 0.25rem;
  cursor: pointer;
  width: 18px;
  height: 18px;
  flex-shrink: 0;
`;

const PolicyContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const PolicyHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const PolicyName = styled.div`
  font-size: 1rem;
  font-weight: 600;
  color: #111827;
`;

const PolicyId = styled.div`
  font-size: 0.875rem;
  color: #6b7280;
  font-family: monospace;
`;

const PolicyDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #4b5563;
`;

const PolicyDetailRow = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const PolicyDetailLabel = styled.span`
  font-weight: 600;
  color: #374151;
`;

const PolicyDetailValue = styled.span`
  color: #6b7280;
`;

const LoadingText = styled.div`
  padding: 2rem;
  text-align: center;
  color: #6b7280;
  font-size: 0.875rem;
`;

const ModalFooter = styled.div`
  padding: 1.5rem 2rem;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  flex-shrink: 0;
`;

const UpdatePolicyButton = styled.button`
  background: #3b82f6;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: #2563eb;
    transform: translateY(-1px);
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

function Dashboard() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [batchesCount, setBatchesCount] = useState(0);
  const [cardsCount, setCardsCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [isPolicyModalOpen, setIsPolicyModalOpen] = useState(false);
  const [fulfillmentPolicies, setFulfillmentPolicies] = useState([]);
  const [paymentPolicies, setPaymentPolicies] = useState([]);
  const [returnPolicies, setReturnPolicies] = useState([]);
  const [isLoadingPolicies, setIsLoadingPolicies] = useState(false);
  const [selectedFulfillmentPolicy, setSelectedFulfillmentPolicy] = useState("");
  const [selectedPaymentPolicy, setSelectedPaymentPolicy] = useState("");
  const [selectedReturnPolicy, setSelectedReturnPolicy] = useState("");
  const [isUpdatingPolicies, setIsUpdatingPolicies] = useState(false);
  const requiredPolicies = useSelector((state) => state.ebay.requiredPolicies);

  const handleOpenPolicyModal = () => {
    navigate(POLICIES);
  };

  const fetchUserPolicies = async () => {
    try {
      const response = await ApiCollections.getUserPolicies();
      const userPolicies = response?.data || {};
      
      // Set selected policies if they exist (handle both snake_case and camelCase)
      if (userPolicies.fulfillment_policy_id || userPolicies.fulfillmentPolicyId) {
        setSelectedFulfillmentPolicy(userPolicies.fulfillment_policy_id || userPolicies.fulfillmentPolicyId);
      }
      if (userPolicies.payment_policy_id || userPolicies.paymentPolicyId) {
        setSelectedPaymentPolicy(userPolicies.payment_policy_id || userPolicies.paymentPolicyId);
      }
      if (userPolicies.return_policy_id || userPolicies.returnPolicyId) {
        setSelectedReturnPolicy(userPolicies.return_policy_id || userPolicies.returnPolicyId);
      }
    } catch (error) {
      console.error("Error fetching user policies:", error);
    }
  };

  const handleClosePolicyModal = () => {
    setIsPolicyModalOpen(false);
  };

  const fetchPolicies = async () => {
    setIsLoadingPolicies(true);
    try {
      const response = await ApiCollections.getAllPolicies({ marketplace_id: "EBAY_DE" });
      
      // Handle both cases: array directly or wrapped in data property
      const policiesArray = Array.isArray(response?.data) 
        ? response.data 
        : (Array.isArray(response) ? response : []);

      console.log("Policies response:", policiesArray);

      // Initialize policy arrays
      let fulfillmentPolicies = [];
      let paymentPolicies = [];
      let returnPolicies = [];

      // Map through the array and extract policies based on type
      policiesArray.forEach((policyItem) => {
        // Check if status code is success (200)
        if (policyItem.status_code === 200 && policyItem.data) {
          if (policyItem.type === "fulfillment" && policyItem.data.fulfillmentPolicies) {
            fulfillmentPolicies = Array.isArray(policyItem.data.fulfillmentPolicies) 
              ? policyItem.data.fulfillmentPolicies 
              : [];
          } else if (policyItem.type === "payment" && policyItem.data.paymentPolicies) {
            paymentPolicies = Array.isArray(policyItem.data.paymentPolicies) 
              ? policyItem.data.paymentPolicies 
              : [];
          } else if (policyItem.type === "return" && policyItem.data.returnPolicies) {
            returnPolicies = Array.isArray(policyItem.data.returnPolicies) 
              ? policyItem.data.returnPolicies 
              : [];
          }
        } else if (policyItem.data?.errors) {
          // Handle error responses
          const errorMessages = policyItem.data.errors.map(err => err.longMessage || err.message).join(", ");
          console.error(`Error fetching ${policyItem.type} policies:`, errorMessages);
        }
      });

      console.log("Extracted policies:", { fulfillmentPolicies, paymentPolicies, returnPolicies });

      setFulfillmentPolicies(fulfillmentPolicies);
      setPaymentPolicies(paymentPolicies);
      setReturnPolicies(returnPolicies);
    } catch (error) {
      console.error("Error fetching policies:", error);
    } finally {
      setIsLoadingPolicies(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClosePolicyModal();
    }
  };

  const handleFulfillmentPolicyChange = (e) => {
    setSelectedFulfillmentPolicy(e.target.value);
  };

  const handlePaymentPolicyChange = (e) => {
    setSelectedPaymentPolicy(e.target.value);
  };

  const handleReturnPolicyChange = (e) => {
    setSelectedReturnPolicy(e.target.value);
  };

  const handleUpdatePolicies = async () => {
    // Build payload with only selected policies
    const payload = {};
    
    if (selectedFulfillmentPolicy) {
      payload.fulfillment_policy_id = selectedFulfillmentPolicy;
    }
    if (selectedPaymentPolicy) {
      payload.payment_policy_id = selectedPaymentPolicy;
    }
    if (selectedReturnPolicy) {
      payload.return_policy_id = selectedReturnPolicy;
    }

    // At least one policy must be selected
    if (Object.keys(payload).length === 0) {
      toastify("warn", "Please select at least one policy to update.");
      return;
    }

    try {
      setIsUpdatingPolicies(true);
      const response = await ApiCollections.updatePolicies(payload);
      console.log("Policies updated successfully:", response);
      toastify("success", "Policies updated successfully!");
      handleClosePolicyModal();
      // Reset selections
      setSelectedFulfillmentPolicy("");
      setSelectedPaymentPolicy("");
      setSelectedReturnPolicy("");
    } catch (error) {
      console.error("Error updating policies:", error);
      toastify("error", error?.response?.data?.message || error?.message || "Failed to update policies. Please try again.");
    } finally {
      setIsUpdatingPolicies(false);
    }
  };

  const ensureHttps = (url) => {
    if (!url) return null;
    if (url.startsWith("http://") && !url.includes("localhost")) {
      return url.replace("http://", "https://");
    }
    return url;
  };

  const DEFAULT_BASE_URL = "http://localhost:8000";
  const API_BASE_URL = ensureHttps(
    import.meta.env.VITE_BASE_URL_DEVELOPMENT || DEFAULT_BASE_URL
  );

  const refreshAccessToken = async () => {
    const refreshToken = localStorage.getItem("refresh");
    if (!refreshToken) {
      // Show toast message and logout when no refresh token is found
      toastify("error", "No refresh access found. Please login again.");
      dispatch(signOutRequest({ func: () => {} }));
      throw new Error("No refresh token found. Please login again.");
    }

    const refreshResponse = await fetch(
      `${API_BASE_URL}/api/v1/token/refresh/`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh: refreshToken }),
      }
    );

    if (!refreshResponse.ok) {
      localStorage.removeItem("access");
      localStorage.removeItem("refresh");
      // Show toast message and logout when refresh token fails
      toastify("error", "No refresh access found. Please login again.");
      dispatch(signOutRequest({ func: () => {} }));
      throw new Error("Failed to refresh token. Please login again.");
    }

    const refreshData = await refreshResponse.json();
    localStorage.setItem("access", refreshData.access);
    return refreshData.access;
  };

  const fetchCounts = async () => {
    try {
      setIsLoading(true);
      setError("");

      let token = localStorage.getItem("access");
      if (!token) token = await refreshAccessToken();

      // Fetch batches count
      const batchesResponse = await fetch(
        `${API_BASE_URL}/api/v1/cards/batches/?page_size=1`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (batchesResponse.status === 401) {
        const newToken = await refreshAccessToken();
        const retryBatchesResponse = await fetch(
          `${API_BASE_URL}/api/v1/cards/batches/?page_size=1`,
          {
            headers: {
              Authorization: `Bearer ${newToken}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (!retryBatchesResponse.ok) {
          throw new Error(
            `Failed to fetch batches: ${retryBatchesResponse.status}`
          );
        }

        const batchesData = await retryBatchesResponse.json();
        setBatchesCount(batchesData.count || 0);
      } else if (!batchesResponse.ok) {
        throw new Error(`Failed to fetch batches: ${batchesResponse.status}`);
      } else {
        const batchesData = await batchesResponse.json();
        setBatchesCount(batchesData.count || 0);
      }

      // Fetch cards count
      const cardsResponse = await fetch(
        `${API_BASE_URL}/api/v1/cards/?page_size=1`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (cardsResponse.status === 401) {
        const newToken = await refreshAccessToken();
        const retryCardsResponse = await fetch(
          `${API_BASE_URL}/api/v1/cards/?page_size=1`,
          {
            headers: {
              Authorization: `Bearer ${newToken}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (!retryCardsResponse.ok) {
          throw new Error(
            `Failed to fetch cards: ${retryCardsResponse.status}`
          );
        }

        const cardsData = await retryCardsResponse.json();
        setCardsCount(cardsData.count || 0);
      } else if (!cardsResponse.ok) {
        throw new Error(`Failed to fetch cards: ${cardsResponse.status}`);
      } else {
        const cardsData = await cardsResponse.json();
        setCardsCount(cardsData.count || 0);
      }
    } catch (err) {
      setError(err?.message || "Failed to load dashboard data");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCounts();
  }, []);

  if (isLoading) {
    return (
      <DashboardContainer>
        <LoadingState>Loading dashboard...</LoadingState>
      </DashboardContainer>
    );
  }

  if (error) {
    return (
      <DashboardContainer>
        <ErrorState>{error}</ErrorState>
      </DashboardContainer>
    );
  }

  return (
    <>
      <DashboardContainer>
        {!requiredPolicies && (
          <PolicyAlertContainer>
            <PolicyAlertMessage>Policies not updated</PolicyAlertMessage>
            <PolicyModalButton onClick={handleOpenPolicyModal}>
              Update Policies
            </PolicyModalButton>
          </PolicyAlertContainer>
        )}
        <CardsGrid>
          <StatCard onClick={() => navigate("/batches")}>
            <StatIcon>
              <Boxes size={22} />
            </StatIcon>
            <StatCardBody>
              <StatCardTitle>Total Batches</StatCardTitle>
              <StatCardValue>{batchesCount.toLocaleString()}</StatCardValue>
            </StatCardBody>
          </StatCard>

          <StatCard onClick={() => navigate("/batches/1/cards")}>
            <StatIcon $bg="#f5f3ff" $color="#8b5cf6">
              <Layers size={22} />
            </StatIcon>
            <StatCardBody>
              <StatCardTitle>Total Cards</StatCardTitle>
              <StatCardValue>{cardsCount.toLocaleString()}</StatCardValue>
            </StatCardBody>
          </StatCard>
        </CardsGrid>
      </DashboardContainer>

      <ModalOverlay $open={isPolicyModalOpen} onClick={handleOverlayClick}>
        <ModalContainer>
          <ModalHeader>
            <ModalTitle>Update Policies</ModalTitle>
            <CloseButton onClick={handleClosePolicyModal}>
              <X size={24} />
            </CloseButton>
          </ModalHeader>
          <ModalContent>
            {isLoadingPolicies ? (
              <LoadingText>Loading policies...</LoadingText>
            ) : (
              <>
                <PolicySection>
                  <PolicySectionTitle>Fulfillment Policies</PolicySectionTitle>
                  <PolicyList>
                    {fulfillmentPolicies.length > 0 ? (
                      fulfillmentPolicies.map((policy) => (
                        <PolicyItem key={policy.fulfillmentPolicyId}>
                          <RadioButton
                            type="radio"
                            name="fulfillmentPolicy"
                            value={policy.fulfillmentPolicyId}
                            checked={selectedFulfillmentPolicy === policy.fulfillmentPolicyId}
                            onChange={handleFulfillmentPolicyChange}
                          />
                          <PolicyContent>
                            <PolicyHeader>
                              <PolicyName>{policy.name}</PolicyName>
                              <PolicyId>ID: {policy.fulfillmentPolicyId}</PolicyId>
                            </PolicyHeader>
                            <PolicyDetails>
                              <PolicyDetailRow>
                                <PolicyDetailLabel>Marketplace:</PolicyDetailLabel>
                                <PolicyDetailValue>{policy.marketplaceId}</PolicyDetailValue>
                              </PolicyDetailRow>
                              {policy.handlingTime && (
                                <PolicyDetailRow>
                                  <PolicyDetailLabel>Handling Time:</PolicyDetailLabel>
                                  <PolicyDetailValue>
                                    {policy.handlingTime.value} {policy.handlingTime.unit}(s)
                                  </PolicyDetailValue>
                                </PolicyDetailRow>
                              )}
                              {policy.shipToLocations && Object.keys(policy.shipToLocations).length > 0 && (
                                <PolicyDetailRow>
                                  <PolicyDetailLabel>Ship To Locations:</PolicyDetailLabel>
                                  <PolicyDetailValue>
                                    {JSON.stringify(policy.shipToLocations)}
                                  </PolicyDetailValue>
                                </PolicyDetailRow>
                              )}
                              {policy.shippingOptions && policy.shippingOptions.length > 0 && (
                                <>
                                  {policy.shippingOptions.map((option, optionIdx) => (
                                    <div key={optionIdx} style={{ marginTop: optionIdx > 0 ? '0.75rem' : '0', paddingTop: optionIdx > 0 ? '0.75rem' : '0', borderTop: optionIdx > 0 ? '1px solid #e5e7eb' : 'none' }}>
                                      <PolicyDetailRow>
                                        <PolicyDetailLabel>Shipping Option {optionIdx + 1}:</PolicyDetailLabel>
                                        <PolicyDetailValue>{option.optionType}</PolicyDetailValue>
                                      </PolicyDetailRow>
                                      {option.costType && (
                                        <PolicyDetailRow>
                                          <PolicyDetailLabel>Cost Type:</PolicyDetailLabel>
                                          <PolicyDetailValue>{option.costType}</PolicyDetailValue>
                                        </PolicyDetailRow>
                                      )}
                                      {option.shippingDiscountProfileId !== undefined && (
                                        <PolicyDetailRow>
                                          <PolicyDetailLabel>Shipping Discount Profile ID:</PolicyDetailLabel>
                                          <PolicyDetailValue>{option.shippingDiscountProfileId}</PolicyDetailValue>
                                        </PolicyDetailRow>
                                      )}
                                      {option.shippingPromotionOffered !== undefined && (
                                        <PolicyDetailRow>
                                          <PolicyDetailLabel>Shipping Promotion Offered:</PolicyDetailLabel>
                                          <PolicyDetailValue>{option.shippingPromotionOffered ? 'Yes' : 'No'}</PolicyDetailValue>
                                        </PolicyDetailRow>
                                      )}
                                      {option.shippingServices && option.shippingServices.length > 0 && (
                                        <>
                                          {option.shippingServices.map((service, serviceIdx) => (
                                            <div key={serviceIdx} style={{ marginLeft: '1rem', marginTop: '0.5rem', paddingLeft: '0.75rem', borderLeft: '2px solid #d1d5db' }}>
                                              <PolicyDetailRow>
                                                <PolicyDetailLabel>Service {serviceIdx + 1}:</PolicyDetailLabel>
                                                <PolicyDetailValue></PolicyDetailValue>
                                              </PolicyDetailRow>
                                              {service.sortOrder !== undefined && (
                                                <PolicyDetailRow>
                                                  <PolicyDetailLabel>Sort Order:</PolicyDetailLabel>
                                                  <PolicyDetailValue>{service.sortOrder}</PolicyDetailValue>
                                                </PolicyDetailRow>
                                              )}
                                              {service.shippingCarrierCode && (
                                                <PolicyDetailRow>
                                                  <PolicyDetailLabel>Carrier Code:</PolicyDetailLabel>
                                                  <PolicyDetailValue>{service.shippingCarrierCode}</PolicyDetailValue>
                                                </PolicyDetailRow>
                                              )}
                                              {service.shippingServiceCode && (
                                                <PolicyDetailRow>
                                                  <PolicyDetailLabel>Service Code:</PolicyDetailLabel>
                                                  <PolicyDetailValue>{service.shippingServiceCode}</PolicyDetailValue>
                                                </PolicyDetailRow>
                                              )}
                                              {service.shippingCost && (
                                                <PolicyDetailRow>
                                                  <PolicyDetailLabel>Shipping Cost:</PolicyDetailLabel>
                                                  <PolicyDetailValue>
                                                    {service.shippingCost.value} {service.shippingCost.currency}
                                                  </PolicyDetailValue>
                                                </PolicyDetailRow>
                                              )}
                                              {service.additionalShippingCost && (
                                                <PolicyDetailRow>
                                                  <PolicyDetailLabel>Additional Shipping Cost:</PolicyDetailLabel>
                                                  <PolicyDetailValue>
                                                    {service.additionalShippingCost.value} {service.additionalShippingCost.currency}
                                                  </PolicyDetailValue>
                                                </PolicyDetailRow>
                                              )}
                                              {service.freeShipping !== undefined && (
                                                <PolicyDetailRow>
                                                  <PolicyDetailLabel>Free Shipping:</PolicyDetailLabel>
                                                  <PolicyDetailValue>{service.freeShipping ? 'Yes' : 'No'}</PolicyDetailValue>
                                                </PolicyDetailRow>
                                              )}
                                              {service.buyerResponsibleForShipping !== undefined && (
                                                <PolicyDetailRow>
                                                  <PolicyDetailLabel>Buyer Responsible For Shipping:</PolicyDetailLabel>
                                                  <PolicyDetailValue>{service.buyerResponsibleForShipping ? 'Yes' : 'No'}</PolicyDetailValue>
                                                </PolicyDetailRow>
                                              )}
                                              {service.buyerResponsibleForPickup !== undefined && (
                                                <PolicyDetailRow>
                                                  <PolicyDetailLabel>Buyer Responsible For Pickup:</PolicyDetailLabel>
                                                  <PolicyDetailValue>{service.buyerResponsibleForPickup ? 'Yes' : 'No'}</PolicyDetailValue>
                                                </PolicyDetailRow>
                                              )}
                                            </div>
                                          ))}
                                        </>
                                      )}
                                    </div>
                                  ))}
                                </>
                              )}
                              {policy.categoryTypes && policy.categoryTypes.length > 0 && (
                                <>
                                  <PolicyDetailRow>
                                    <PolicyDetailLabel>Category Types:</PolicyDetailLabel>
                                    <PolicyDetailValue></PolicyDetailValue>
                                  </PolicyDetailRow>
                                  {policy.categoryTypes.map((cat, idx) => (
                                    <PolicyDetailRow key={idx} style={{ marginLeft: '1rem' }}>
                                      <PolicyDetailLabel>{cat.name}:</PolicyDetailLabel>
                                      <PolicyDetailValue>Default: {cat.default ? 'Yes' : 'No'}</PolicyDetailValue>
                                    </PolicyDetailRow>
                                  ))}
                                </>
                              )}
                              <PolicyDetailRow>
                                <PolicyDetailLabel>Global Shipping:</PolicyDetailLabel>
                                <PolicyDetailValue>{policy.globalShipping ? 'Yes' : 'No'}</PolicyDetailValue>
                              </PolicyDetailRow>
                              <PolicyDetailRow>
                                <PolicyDetailLabel>Pickup Drop Off:</PolicyDetailLabel>
                                <PolicyDetailValue>{policy.pickupDropOff ? 'Yes' : 'No'}</PolicyDetailValue>
                              </PolicyDetailRow>
                              <PolicyDetailRow>
                                <PolicyDetailLabel>Freight Shipping:</PolicyDetailLabel>
                                <PolicyDetailValue>{policy.freightShipping ? 'Yes' : 'No'}</PolicyDetailValue>
                              </PolicyDetailRow>
                            </PolicyDetails>
                          </PolicyContent>
                        </PolicyItem>
                      ))
                    ) : (
                      <LoadingText>No fulfillment policies available</LoadingText>
                    )}
                  </PolicyList>
                </PolicySection>

                <PolicySection>
                  <PolicySectionTitle>Payment Policies</PolicySectionTitle>
                  <PolicyList>
                    {paymentPolicies.length > 0 ? (
                      paymentPolicies.map((policy) => (
                        <PolicyItem key={policy.paymentPolicyId}>
                          <RadioButton
                            type="radio"
                            name="paymentPolicy"
                            value={policy.paymentPolicyId}
                            checked={selectedPaymentPolicy === policy.paymentPolicyId}
                            onChange={handlePaymentPolicyChange}
                          />
                          <PolicyContent>
                            <PolicyHeader>
                              <PolicyName>{policy.name}</PolicyName>
                              <PolicyId>ID: {policy.paymentPolicyId}</PolicyId>
                            </PolicyHeader>
                            <PolicyDetails>
                              {policy.marketplaceId && (
                                <PolicyDetailRow>
                                  <PolicyDetailLabel>Marketplace:</PolicyDetailLabel>
                                  <PolicyDetailValue>{policy.marketplaceId}</PolicyDetailValue>
                                </PolicyDetailRow>
                              )}
                              {policy.immediatePay !== undefined && (
                                <PolicyDetailRow>
                                  <PolicyDetailLabel>Immediate Pay:</PolicyDetailLabel>
                                  <PolicyDetailValue>{policy.immediatePay ? 'Yes' : 'No'}</PolicyDetailValue>
                                </PolicyDetailRow>
                              )}
                              {policy.paymentMethods && policy.paymentMethods.length > 0 ? (
                                <>
                                  <PolicyDetailRow>
                                    <PolicyDetailLabel>Payment Methods:</PolicyDetailLabel>
                                    <PolicyDetailValue></PolicyDetailValue>
                                  </PolicyDetailRow>
                                  {policy.paymentMethods.map((method, idx) => (
                                    <PolicyDetailRow key={idx} style={{ marginLeft: '1rem' }}>
                                      <PolicyDetailLabel>
                                        {method.paymentMethodType || `Method ${idx + 1}`}
                                      </PolicyDetailLabel>
                                      <PolicyDetailValue></PolicyDetailValue>
                                    </PolicyDetailRow>
                                  ))}
                                </>
                              ) : (
                                <PolicyDetailRow>
                                  <PolicyDetailLabel>Payment Methods:</PolicyDetailLabel>
                                  <PolicyDetailValue>None</PolicyDetailValue>
                                </PolicyDetailRow>
                              )}
                              {policy.categoryTypes && policy.categoryTypes.length > 0 && (
                                <>
                                  <PolicyDetailRow>
                                    <PolicyDetailLabel>Category Types:</PolicyDetailLabel>
                                    <PolicyDetailValue></PolicyDetailValue>
                                  </PolicyDetailRow>
                                  {policy.categoryTypes.map((cat, idx) => (
                                    <PolicyDetailRow key={idx} style={{ marginLeft: '1rem' }}>
                                      <PolicyDetailLabel>{cat.name}:</PolicyDetailLabel>
                                      <PolicyDetailValue>Default: {cat.default ? 'Yes' : 'No'}</PolicyDetailValue>
                                    </PolicyDetailRow>
                                  ))}
                                </>
                              )}
                              {policy.paymentInstructions && (
                                <PolicyDetailRow>
                                  <PolicyDetailLabel>Instructions:</PolicyDetailLabel>
                                  <PolicyDetailValue>{policy.paymentInstructions}</PolicyDetailValue>
                                </PolicyDetailRow>
                              )}
                            </PolicyDetails>
                          </PolicyContent>
                        </PolicyItem>
                      ))
                    ) : (
                      <LoadingText>No payment policies available</LoadingText>
                    )}
                  </PolicyList>
                </PolicySection>

                <PolicySection>
                  <PolicySectionTitle>Return Policies</PolicySectionTitle>
                  <PolicyList>
                    {returnPolicies.length > 0 ? (
                      returnPolicies.map((policy) => (
                        <PolicyItem key={policy.returnPolicyId}>
                          <RadioButton
                            type="radio"
                            name="returnPolicy"
                            value={policy.returnPolicyId}
                            checked={selectedReturnPolicy === policy.returnPolicyId}
                            onChange={handleReturnPolicyChange}
                          />
                          <PolicyContent>
                            <PolicyHeader>
                              <PolicyName>{policy.name}</PolicyName>
                              <PolicyId>ID: {policy.returnPolicyId}</PolicyId>
                            </PolicyHeader>
                            <PolicyDetails>
                              {policy.marketplaceId && (
                                <PolicyDetailRow>
                                  <PolicyDetailLabel>Marketplace:</PolicyDetailLabel>
                                  <PolicyDetailValue>{policy.marketplaceId}</PolicyDetailValue>
                                </PolicyDetailRow>
                              )}
                              {policy.returnsAccepted !== undefined && (
                                <PolicyDetailRow>
                                  <PolicyDetailLabel>Returns Accepted:</PolicyDetailLabel>
                                  <PolicyDetailValue>{policy.returnsAccepted ? 'Yes' : 'No'}</PolicyDetailValue>
                                </PolicyDetailRow>
                              )}
                              {policy.categoryTypes && policy.categoryTypes.length > 0 && (
                                <>
                                  <PolicyDetailRow>
                                    <PolicyDetailLabel>Category Types:</PolicyDetailLabel>
                                    <PolicyDetailValue></PolicyDetailValue>
                                  </PolicyDetailRow>
                                  {policy.categoryTypes.map((cat, idx) => (
                                    <PolicyDetailRow key={idx} style={{ marginLeft: '1rem' }}>
                                      <PolicyDetailLabel>{cat.name}</PolicyDetailLabel>
                                      <PolicyDetailValue></PolicyDetailValue>
                                    </PolicyDetailRow>
                                  ))}
                                </>
                              )}
                              {policy.refundMethod && (
                                <PolicyDetailRow>
                                  <PolicyDetailLabel>Refund Method:</PolicyDetailLabel>
                                  <PolicyDetailValue>{policy.refundMethod}</PolicyDetailValue>
                                </PolicyDetailRow>
                              )}
                              {policy.returnPeriod && (
                                <PolicyDetailRow>
                                  <PolicyDetailLabel>Return Period:</PolicyDetailLabel>
                                  <PolicyDetailValue>
                                    {policy.returnPeriod.value} {policy.returnPeriod.unit}(s)
                                  </PolicyDetailValue>
                                </PolicyDetailRow>
                              )}
                              {policy.returnShippingCostPayer && (
                                <PolicyDetailRow>
                                  <PolicyDetailLabel>Shipping Cost Payer:</PolicyDetailLabel>
                                  <PolicyDetailValue>{policy.returnShippingCostPayer}</PolicyDetailValue>
                                </PolicyDetailRow>
                              )}
                              {policy.restockingFeePercentage !== undefined && (
                                <PolicyDetailRow>
                                  <PolicyDetailLabel>Restocking Fee:</PolicyDetailLabel>
                                  <PolicyDetailValue>{policy.restockingFeePercentage}%</PolicyDetailValue>
                                </PolicyDetailRow>
                              )}
                              {policy.description && (
                                <PolicyDetailRow>
                                  <PolicyDetailLabel>Description:</PolicyDetailLabel>
                                  <PolicyDetailValue>{policy.description}</PolicyDetailValue>
                                </PolicyDetailRow>
                              )}
                              {policy.extendedHolidayReturnsOffered !== undefined && (
                                <PolicyDetailRow>
                                  <PolicyDetailLabel>Extended Holiday Returns:</PolicyDetailLabel>
                                  <PolicyDetailValue>{policy.extendedHolidayReturnsOffered ? 'Yes' : 'No'}</PolicyDetailValue>
                                </PolicyDetailRow>
                              )}
                            </PolicyDetails>
                          </PolicyContent>
                        </PolicyItem>
                      ))
                    ) : (
                      <LoadingText>No return policies available</LoadingText>
                    )}
                  </PolicyList>
                </PolicySection>
              </>
            )}
          </ModalContent>
          <ModalFooter>
            <UpdatePolicyButton onClick={handleUpdatePolicies} disabled={isUpdatingPolicies}>
              {isUpdatingPolicies ? "Updating..." : "Update Policies"}
            </UpdatePolicyButton>
          </ModalFooter>
        </ModalContainer>
      </ModalOverlay>
    </>
  );
}

export default Dashboard;
